import type { Mode } from '../domain/questions';
import type { Units } from '../domain/units';

const STORAGE_KEY = 'dcd:mode';
const UNITS_KEY = 'dcd:units';

/**
 * How many questions the user wants asked, remembered between visits.
 *
 * Kept apart from the stored location deliberately: clearing one should not clear the
 * other, and this is a preference rather than a cache. Anything unrecognised in storage
 * reads as null, so a stale or hand-edited value falls back to the default rather than
 * building a question set for a mode that no longer exists.
 */
export function readStoredMode(): Mode | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'simple' || raw === 'advanced' ? raw : null;
  } catch {
    return null;
  }
}

export function storeMode(mode: Mode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Private mode; keeping it for this session is good enough.
  }
}

/**
 * Miles or kilometres. Read the same defensive way as the mode: anything that isn't one
 * of the two known values reads as null, so the caller's default applies.
 */
export function readStoredUnits(): Units | null {
  try {
    const raw = localStorage.getItem(UNITS_KEY);
    return raw === 'imperial' || raw === 'metric' ? raw : null;
  } catch {
    return null;
  }
}

export function storeUnits(units: Units): void {
  try {
    localStorage.setItem(UNITS_KEY, units);
  } catch {
    // Same as the mode: this session is enough.
  }
}
